import * as React from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

export type CultureMapData = {
  culture: string;
  alternate_names?: string[];
  wikipedia_url?: string | null;
  location_description?: string;
  countries?: string[];
  coordinates?: { lat: number; lon: number } | null;
  geojson?: any | null;
};

/**
 * Extract a map position for a culture.
 * Prefers centroid of any GeoJSON polygon over the raw coordinates field,
 * so we always render a pin rather than a bounding-box rectangle.
 */
function getPosition(culture: CultureMapData): [number, number] | null {
  if (culture.geojson) {
    try {
      const ring: number[][] =
        culture.geojson.type === "Polygon"
          ? culture.geojson.coordinates[0]
          : culture.geojson.type === "MultiPolygon"
            ? culture.geojson.coordinates[0][0]
            : null;
      if (ring?.length) {
        const lat = ring.reduce((s, c) => s + c[1], 0) / ring.length;
        const lon = ring.reduce((s, c) => s + c[0], 0) / ring.length;
        return [lat, lon];
      }
    } catch {
      // fall through to coordinates
    }
  }
  if (culture.coordinates) {
    return [culture.coordinates.lat, culture.coordinates.lon];
  }
  return null;
}

/** Flies the map to a target position when it changes. */
const MapController: React.FC<{ flyTarget: [number, number] | null }> = ({
  flyTarget,
}) => {
  const map = useMap();
  React.useEffect(() => {
    if (flyTarget) {
      map.flyTo(flyTarget, 6, { duration: 1.2, easeLinearity: 0.3 });
    }
  }, [flyTarget, map]);
  return null;
};

interface CultureMapProps {
  culturesData: CultureMapData[];
  culturesToHighlight: Set<string>;
  /** When a single entry matches this string the map auto-pans to it. */
  filterSearch: string;
  /** Called on click; parent should update filters AND fire search. */
  onCultureClick: (culture: CultureMapData) => void;
  height?: number;
}

export const CultureMap: React.FC<CultureMapProps> = ({
  culturesData,
  culturesToHighlight,
  filterSearch,
  onCultureClick,
  height = 300,
}) => {
  // Derive a flyTo target when filterSearch narrows to exactly one match
  const flyTarget = React.useMemo<[number, number] | null>(() => {
    const q = filterSearch.trim().toLowerCase();
    if (!q) return null;
    const matches = culturesData.filter(
      (c) =>
        c.culture.toLowerCase().includes(q) ||
        c.alternate_names?.some((n) => n.toLowerCase().includes(q)),
    );
    if (matches.length === 1) return getPosition(matches[0]);
    return null;
  }, [filterSearch, culturesData]);

  return (
    <div style={{ height, width: "100%" }} className="border border-theme-color">
      <MapContainer
        center={[2, 20]}
        zoom={3}
        minZoom={2}
        maxZoom={10}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController flyTarget={flyTarget} />

        {culturesData.map((culture, idx) => {
          const pos = getPosition(culture);
          if (!pos) return null;
          const isSelected = culturesToHighlight.has(culture.culture);

          return (
            <CircleMarker
              key={idx}
              center={pos}
              // Selected pins are slightly larger; both are clearly visible
              radius={isSelected ? 7 : 5}
              pathOptions={{
                fillColor: isSelected ? "#ff6b6b" : "#4ecdc4",
                color: isSelected ? "#c92a2a" : "#2d9cdb",
                weight: isSelected ? 2 : 1,
                opacity: isSelected ? 1 : 0.55,
                fillOpacity: isSelected ? 0.88 : 0.45,
              }}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stopPropagation(e);
                  onCultureClick(culture);
                },
              }}
            >
              {/* Selected cultures: always-visible name label */}
              {isSelected && (
                <Tooltip
                  permanent
                  direction="top"
                  offset={[0, -8]}
                  opacity={1}
                  className="culture-label-permanent"
                >
                  <span style={{ fontFamily: "monospace", fontWeight: "bold", fontSize: "11px" }}>
                    {culture.culture}
                  </span>
                </Tooltip>
              )}

              {/* Unselected cultures: hover tooltip with name + detail */}
              {!isSelected && (
                <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                  <div style={{ fontFamily: "monospace", maxWidth: "200px" }}>
                    <div style={{ fontWeight: "bold", fontSize: "12px" }}>
                      {culture.culture}
                    </div>
                    {culture.location_description && (
                      <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "2px" }}>
                        {culture.location_description}
                      </div>
                    )}
                    {culture.countries && culture.countries.length > 0 && (
                      <div style={{ fontSize: "10px", opacity: 0.65, marginTop: "2px" }}>
                        {culture.countries.join(", ")}
                      </div>
                    )}
                    <div style={{ fontSize: "10px", opacity: 0.5, marginTop: "3px" }}>
                      Click to filter
                    </div>
                  </div>
                </Tooltip>
              )}
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
};
